import { createElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { store, useSelector, dispatch } from 'store';
import { refreshWhatsappTemplatesSilently } from 'store/slices/templates';
import {
  setTemplateWatcherLastSuccessfulSyncAt,
  setTemplateWatcherSyncing,
} from 'store/slices/template-watcher';
import type { WhatsappTemplate, WhatsappTemplateStatus } from 'types/templates';
import {
  getTemplateCreatedAtTime,
  getWhatsappTemplateHebrewDisplayName,
} from 'utils/template-display';

const FIVE_MIN_MS = 5 * 60 * 1000;
const FIFTEEN_MIN_MS = 15 * 60 * 1000;
const FORTY_EIGHT_H_MS = 48 * 60 * 60 * 1000;

function buildStatusSnapshot(templates: WhatsappTemplate[]): Record<string, WhatsappTemplateStatus> {
  const snap: Record<string, WhatsappTemplateStatus> = {};
  for (const t of templates) {
    if (t.id != null && t.id !== '') {
      snap[String(t.id)] = t.templateStatus;
    }
  }
  return snap;
}

export function useTemplateWatcher() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((s) => s.user);
  const whatsappTemplates = useSelector((s) => s.templates.whatsappTemplates);

  const prevSnapshotRef = useRef<Record<string, WhatsappTemplateStatus> | null>(null);
  const baselineEstablishedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const didHydrateRef = useRef(false);

  const pendingTemplates = useMemo(
    () => whatsappTemplates.filter((t) => t.templateStatus === 'PENDING'),
    [whatsappTemplates],
  );

  const hasStalePending = useMemo(() => {
    const now = Date.now();
    return pendingTemplates.some((t) => {
      const created = getTemplateCreatedAtTime(t);
      if (created == null) return false;
      return now - created > FORTY_EIGHT_H_MS;
    });
  }, [pendingTemplates]);

  const applyTransitionToasts = useCallback(
    (templates: WhatsappTemplate[]) => {
      const next = buildStatusSnapshot(templates);
      if (!baselineEstablishedRef.current) {
        prevSnapshotRef.current = next;
        baselineEstablishedRef.current = true;
        return;
      }
      const prev = prevSnapshotRef.current ?? {};
      for (const [id, now] of Object.entries(next)) {
        const was = prev[id];
        if (was === 'PENDING' && (now === 'APPROVED' || now === 'REJECTED')) {
          const tmpl = templates.find((x) => String(x.id) === id);
          if (!tmpl) continue;
          const name = getWhatsappTemplateHebrewDisplayName(tmpl);
          const isApprove = now === 'APPROVED';
          const msg =
            (isApprove ? 'התבנית אושרה: ' : 'התבנית נדחתה: ') + `"${name}"`;
          enqueueSnackbar(
            createElement<any>(
              Box,
              {
                component: 'span',
                onClick: () => navigate(`/templates/list?highlight=${encodeURIComponent(id)}`),
                sx: { cursor: 'pointer', display: 'block' },
              },
              msg,
            ),
            { variant: isApprove ? 'success' : 'error' },
          );
        }
      }
      prevSnapshotRef.current = next;
    },
    [enqueueSnackbar, navigate],
  );

  const runSync = useCallback(
    async (options?: { force?: boolean }) => {
      if (!user?.Udid) return;
      const force = options?.force === true;
      const last = store.getState().templateWatcher.lastSuccessfulSyncAt;
      if (!force && last != null && Date.now() - last < FIVE_MIN_MS) {
        return;
      }
      if (document.visibilityState !== 'visible') {
        return;
      }

      dispatch(setTemplateWatcherSyncing(true));
      try {
        const ok = await refreshWhatsappTemplatesSilently();
        if (ok) {
          dispatch(setTemplateWatcherLastSuccessfulSyncAt(Date.now()));
          const fresh = store.getState().templates.whatsappTemplates;
          applyTransitionToasts(fresh);
        }
      } finally {
        dispatch(setTemplateWatcherSyncing(false));
      }
    },
    [user?.Udid, applyTransitionToasts],
  );

  const manualSync = useCallback(() => runSync({ force: true }), [runSync]);

  useEffect(() => {
    if (!user?.Udid) {
      baselineEstablishedRef.current = false;
      prevSnapshotRef.current = null;
      didHydrateRef.current = false;
    }
  }, [user?.Udid]);

  useEffect(() => {
    if (!user?.Udid || didHydrateRef.current) return;
    didHydrateRef.current = true;
    void runSync({ force: true });
  }, [user?.Udid, runSync]);

  useEffect(() => {
    if (!user?.Udid || pendingTemplates.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      void runSync({ force: false });
    }, FIFTEEN_MIN_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user?.Udid, pendingTemplates.length, runSync]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible' || !user?.Udid) return;
      if (pendingTemplates.length === 0) return;
      void runSync({ force: false });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [user?.Udid, pendingTemplates.length, runSync]);

  return {
    pendingCount: pendingTemplates.length,
    hasStalePending,
    manualSync,
  };
}
